/**
 * CoS Catalog Section Custom Element
 * Handles interactive features for video playback, overlay triggers, and redirect functionality
 * Uses Splide slider and extends HTMLElement for better encapsulation
 */

class CosCatalogSection extends HTMLElement {
  constructor() {
    super();
    this.splide = null;
  }

  async connectedCallback() {
    // Wait for Splide to be available
    await ResourceCoordinator.requestVendor("Splide");

    // Initialize the component
    this.init();
  }

  disconnectedCallback() {
    // Cleanup when element is removed
    if (this.splide) {
      this.splide.destroy();
    }
  }

  async init() {
    // Initialize Splide
    await this.initializeSplide();

    // Setup component functionality
    this.bindEvents();
    this.setupAccessibility();
    this.setupVideoInteractions();
  }

  async initializeSplide() {
    const splideElement = this.querySelector(".cos-catalog-splide");

    if (splideElement) {
      this.splide = new Splide(splideElement, {
        type: "loop",
        perPage: 4,
        heightRatio: 0.25,
        perMove: 1,
        gap: "32px",
        breakpoints: {
          767: {
            perPage: 1,
            perMove: 1,
            arrows: false,
            heightRatio: 1,
          },
          1023: {
            perPage: 2,
            perMove: 1,
            gap: "16px",
            heightRatio: 0.5,
          },
          1440: {
            gap: "20px",
          },
        },
      }).mount();
    }

    // Remove placeholder
    const placeholder = this.querySelector(".splide-placeholder");
    if (placeholder) {
      placeholder.remove();
    }
  }

  setupVideoInteractions() {
    const cards = this.querySelectorAll("[data-variation]");
    cards.forEach((card) => {
      this.handleCardVideoInteraction(card);
    });
  }

  bindEvents() {
    // Video play button clicks
    this.addEventListener("click", (e) => {
      if (e.target.closest(".cos-catalog-card__play-btn")) {
        e.preventDefault();
        this.handleVideoPlay(e.target.closest(".cos-catalog-card__play-btn"));
      }

      // Overlay trigger clicks
      if (e.target.closest(".cos-catalog-card__overlay-btn")) {
        e.preventDefault();
        this.handleOverlayOpen(
          e.target.closest(".cos-catalog-card__overlay-btn")
        );
      }

      // Overlay close clicks
      if (e.target.closest(".cos-catalog-card__overlay-close")) {
        e.preventDefault();
        this.handleOverlayClose(
          e.target.closest(".cos-catalog-card__overlay-close")
        );
      }

      // Close overlay when clicking backdrop
      if (e.target.classList.contains("cos-catalog-card__overlay-content")) {
        this.handleOverlayClose(
          e.target.querySelector(".cos-catalog-card__overlay-close")
        );
      }

      // CTA button clicks (for tracking/analytics)
      if (e.target.closest(".cos-catalog-card__cta-btn")) {
        this.handleCtaClick(e.target.closest(".cos-catalog-card__cta-btn"));
      }
    });

    // Keyboard events
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeAllOverlays();
      }
    });
  }

  handleVideoPlay(playButton) {
    const card = playButton.closest("[data-variation]");
    const video = card.querySelector("video");

    if (!video) {
      console.warn("No video element found in video card");
      return;
    }

    try {
      if (video.paused) {
        // Play the video
        video.play();
        this.updatePlayButtonState(playButton, "playing");

        // Track video play event
        this.trackEvent("video_play", {
          card_id: card.dataset.blockId,
        });
      } else {
        // Pause the video
        video.pause();
        this.updatePlayButtonState(playButton, "paused");

        // Track video pause event
        this.trackEvent("video_pause", {
          card_id: card.dataset.blockId,
        });
      }
    } catch (error) {
      console.error("Error toggling video playback:", error);
    }
  }

  updatePlayButtonState(playButton, state) {
    const svg = playButton.querySelector("svg");
    if (!svg) return;

    if (state === "playing") {
      // Show pause icon (two vertical bars)
      svg.innerHTML = `
        <path d="M8 3.25C6.4834 3.25 5.25 4.4834 5.25 6V18C5.25 19.5166 6.4834 20.75 8 20.75C9.5166 20.75 10.75 19.5166 10.75 18V6C10.75 4.4834 9.5166 3.25 8 3.25ZM9.25 18C9.25 18.6895 8.68945 19.25 8 19.25C7.31055 19.25 6.75 18.6895 6.75 18V6C6.75 5.31055 7.31055 4.75 8 4.75C8.68945 4.75 9.25 5.31055 9.25 6V18Z" fill="#403833"/>
        <path d="M16 3.25C14.4834 3.25 13.25 4.4834 13.25 6V18C13.25 19.5166 14.4834 20.75 16 20.75C17.5166 20.75 18.75 19.5166 18.75 18V6C18.75 4.4834 17.5166 3.25 16 3.25ZM17.25 18C17.25 18.6895 16.6895 19.25 16 19.25C15.3105 19.25 14.75 18.6895 14.75 18V6C14.75 5.31055 15.3105 4.75 16 4.75C16.6895 4.75 17.25 5.31055 17.25 6V18Z" fill="#403833"/>
      `;
      playButton.setAttribute("aria-label", "Pause Video");
    } else {
      // Show play icon (triangle)
      svg.innerHTML = `
<path d="M19.4717 9.64206L10.3584 4.17429C9.49805 3.6572 8.46094 3.6445 7.58838 4.13913C6.71484 4.63376 6.19336 5.52829 6.19336 6.5322V17.4677C6.19336 18.4717 6.71484 19.3662 7.58838 19.8603C8.01465 20.1025 8.47998 20.2226 8.94434 20.2226C9.43164 20.2226 9.91797 20.0898 10.3584 19.8261L19.4727 14.3584C20.3076 13.8564 20.8066 12.9746 20.8066 11.9995C20.8057 11.0244 20.3076 10.143 19.4717 9.64206ZM18.7002 13.0722L9.58692 18.539C9.19581 18.7763 8.7251 18.7793 8.32764 18.5556C7.93018 18.33 7.69336 17.9238 7.69336 17.4677V6.5322C7.69336 6.07615 7.93018 5.66941 8.32764 5.4448C8.52149 5.33494 8.73291 5.28025 8.94385 5.28025C9.16553 5.28025 9.38623 5.34031 9.58692 5.46043L18.7002 10.9282C19.0801 11.1557 19.3067 11.5566 19.3067 12C19.3067 12.4433 19.0801 12.8447 18.7002 13.0722Z" fill="#403833"/>
      `;
      playButton.setAttribute("aria-label", "Play Video");
    }
  }

  handleOverlayOpen(button) {
    const card = button.closest("[data-variation]");
    const blockId = card.dataset.blockId;
    const overlay = document.querySelector(`[data-overlay-id="${blockId}"]`);

    if (!overlay) {
      console.warn("No overlay content found for overlay card");
      return;
    }

    // Close any existing overlays
    this.closeAllOverlays();

    // Show overlay (change from display:none to display:flex using Tailwind classes)
    overlay.classList.remove("hidden");
    overlay.classList.add("flex");
    document.body.style.overflow = "hidden";

    // Focus management
    const closeButton = overlay.querySelector(
      ".cos-catalog-card__overlay-close"
    );
    if (closeButton) {
      setTimeout(() => closeButton.focus(), 100);
    }

    // Track overlay open event
    this.trackEvent("overlay_open", {
      card_id: card.dataset.blockId,
    });
  }

  handleOverlayClose(closeButton) {
    const overlay = closeButton.closest(".cos-catalog-card__overlay-content");
    if (overlay) {
      overlay.classList.add("hidden");
      overlay.classList.remove("flex");
      document.body.style.overflow = "";

      // Track overlay close event
      const blockId = overlay.dataset.overlayId;
      this.trackEvent("overlay_close", {
        block_id: blockId,
      });
    }
  }

  closeAllOverlays() {
    const overlays = document.querySelectorAll(
      ".cos-catalog-card__overlay-content"
    );
    overlays.forEach((overlay) => {
      overlay.classList.add("hidden");
      overlay.classList.remove("flex");
    });
    document.body.style.overflow = "";
  }

  handleCtaClick(button) {
    const card = button.closest("[data-variation]");
    const href = button.href || button.dataset.href;

    // Track CTA click event
    this.trackEvent("cta_click", {
      card_id: card.dataset.blockId,
      cta_text: button.textContent.trim(),
      cta_url: href,
    });
  }

  setupCardHoverEffects() {
    // Hover effects disabled to prevent unwanted interactions
    // All card interactions now require explicit user clicks
    // const cards = this.section.querySelectorAll("[data-variation]");
    // ... hover functionality removed
  }

  // Method to handle video interactions in cards
  handleCardVideoInteraction(card) {
    const video = card.querySelector("video");
    if (video) {
      const variation = card.dataset.variation;

      // Video hover auto-play is disabled
      // Videos will only play/pause when the user clicks the play button
      if (variation === "video") {
        // Hover auto-play functionality removed to prevent unwanted video playback
        // Users must manually click the play button to start videos
      }
    }
  }

  // Method to pause all videos in the section
  pauseAllVideos() {
    const videos = this.querySelectorAll("video");
    videos.forEach((video) => {
      video.pause();
    });
  }

  setupAccessibility() {
    const cards = this.querySelectorAll("[data-variation]");

    cards.forEach((card) => {
      // Add ARIA labels and roles
      const variation = card.dataset.variation;
      const title = card.querySelector("h3")?.textContent || "Card";

      switch (variation) {
        case "video":
          card.setAttribute("role", "button");
          card.setAttribute("aria-label", `Play video: ${title}`);
          break;
        case "overlay":
          card.setAttribute("role", "button");
          card.setAttribute("aria-label", `Show details for: ${title}`);
          break;
        case "redirect":
          card.setAttribute("role", "link");
          card.setAttribute("aria-label", `Navigate to: ${title}`);
          break;
      }

      // Keyboard navigation
      card.setAttribute("tabindex", "0");
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          card.click();
        }
      });
    });
  }

  trackEvent(eventName, data = {}) {
    // Analytics tracking - integrate with your analytics solution
    if (typeof gtag !== "undefined") {
      gtag("event", eventName, {
        event_category: "cos_catalog_section",
        ...data,
      });
    }

    if (typeof fbq !== "undefined") {
      fbq("trackCustom", `CosCatalog_${eventName}`, data);
    }

    // Console log for debugging
    console.log(`CoS Catalog Event: ${eventName}`, data);
  }
}

// Register the custom element
customElements.define("cos-catalog-section", CosCatalogSection);
